// src/hq/theme.js
//
// The AI HEADQUARTERS design system — single source of truth for the
// "walk into the building" experience. Warm-DARK, cinematic, but built
// for an insurance agent who works in it all day.
//
// Direction (owner-approved, 2026-07):
//   · The website is the building's exterior; the CRM is the interior.
//   · Warm AMBER light inside a DARK cinematic architecture — not black,
//     not cream. The environment wins; the interface floats in it.
//   · TWO MODES: `arrival` (cinematic welcome) and `work` (dense, fast,
//     environment dimmed to a backdrop). Same building; sit down at the
//     desk. See SFA_HQ_DESIGN_SYSTEM.md.
//   · Every metric/employee/priority maps to REAL data — never fake.
//
// This module is additive: it does NOT replace src/lib/tokens.js. HQ
// surfaces consume `hq` from here; legacy surfaces are untouched.

// ── palette (the material system) ──────────────────────────────────
export const color = {
  base:        '#050709', // obsidian base — the room's void
  arch:        '#090C10', // deep architectural surface
  glass:       'rgba(9,12,16,0.60)',  // smoked-glass command surface
  glassSoft:   'rgba(9,12,16,0.52)',
  panel:       'rgba(14,18,23,0.86)', // elevated intelligence panel
  line:        'rgba(245,166,35,0.18)', // thin technical border
  lineActive:  'rgba(245,166,35,0.50)', // active/focused edge
  amber:       '#F5A623', // primary — brand, active, key data
  bright:      '#FFC457', // "intelligence" — AI activity, live pulse, emphasis
  deep:        '#9A6C1E', // pressed / low amber
  text:        '#F4F4F2',
  textSec:     '#B9BEC6',
  textMuted:   '#7C838C',
  ok:          '#5BA36F', // verified success (restrained green)
  risk:        '#F5A623', // needs attention (amber — NOT a new color)
  critical:    '#C4584A', // genuine risk only (muted red)
};

// Semantic color is separate from the amber accent. Amber = brand/energy,
// never "good". Black stays dominant; do not make every panel amber.

// ── per-department "floor" identity (mood lighting + manager) ──────
// Each floor keeps the same building but shifts its light + has its own
// AI-employee manager who greets the operator from REAL data.
export const floors = {
  command:       { label: 'Command Center',   accent: '#F5A623', tint: 'rgba(245,166,35,0.10)', manager: null,        room: '/hq/command-room.jpg' },
  sales:         { label: 'Sales Floor',       accent: '#FFB84D', tint: 'rgba(255,184,77,0.12)', manager: 'CLOSER',    portrait: '/hq/closer.jpg' },
  communications:{ label: 'Communications',    accent: '#6AA9C9', tint: 'rgba(106,169,201,0.12)', manager: 'NOVA',     portrait: '/hq/nova.jpg' },   // blue displays + amber
  revenue:       { label: 'Revenue Command',   accent: '#E4B44E', tint: 'rgba(228,180,78,0.14)', manager: 'LEDGER',   portrait: '/hq/ledger.jpg' }, // gold financial light
  compliance:    { label: 'Compliance Vault',  accent: '#5BA36F', tint: 'rgba(91,163,111,0.12)', manager: 'SENTINEL', portrait: '/hq/sentinel.jpg' }, // cool green verification
  clients:       { label: 'Client Operations', accent: '#E0A45C', tint: 'rgba(224,164,92,0.12)', manager: null,        portrait: null },              // warm concierge
  retention:     { label: 'Retention',         accent: '#D98A56', tint: 'rgba(217,138,86,0.12)', manager: 'MAYA',     portrait: '/hq/maya.jpg' },
  growth:        { label: 'Growth Lab',        accent: '#F08A3C', tint: 'rgba(240,138,60,0.12)', manager: 'ATLAS',    portrait: null },              // orange + maps
  aiworkforce:   { label: 'AI Workforce',      accent: '#F5A623', tint: 'rgba(245,166,35,0.08)', manager: 'ARIA',     portrait: '/hq/aria.jpg', darkest: true },
};

// ── typography ─────────────────────────────────────────────────────
export const type = {
  display: "'Outfit', ui-sans-serif, system-ui, sans-serif", // bold geometric — executive headlines
  serif:   "'Fraunces', Georgia, serif",                     // refined serif — cinematic department statements ONLY
  body:    "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif", // work UI
  mono:    "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",      // status, timestamps, codes, confidence
  scale:   { h1: 'clamp(28px,4.4vw,50px)', h2: '16px', label: '10px', body: '14px', tiny: '11px' },
};

// ── panels / glass ─────────────────────────────────────────────────
export const surface = {
  panel:  { background: color.panel, border: `1px solid ${color.line}`, borderRadius: 10, backdropFilter: 'blur(10px)' },
  glass:  { background: color.glass, border: `1px solid ${color.line}`, borderRadius: 11, backdropFilter: 'blur(12px)' },
  radius: { panel: 10, chip: 999, button: 10 },
  // thin amber top-highlight for the "engineered" edge
  topEdge: `linear-gradient(90deg,transparent,${color.amber},transparent)`,
  shadow:  '0 20px 50px -24px rgba(0,0,0,0.6)',
  glow:    (c = color.amber) => `0 0 26px -14px ${c}`,
};

// ── motion (restrained; motion means real activity) ────────────────
export const motion = {
  arrival:   '2s cubic-bezier(.2,.7,.2,1)',   // camera settling into the room
  floorMove: '250ms cubic-bezier(.4,0,.2,1)', // camera-slide between floors
  reveal:    '.7s ease',
  breatheMs: 4500, // ARIA / live-status breathing
  // prefers-reduced-motion must disable all of the above.
};

// convenience: the two experience modes
export const modes = { ARRIVAL: 'arrival', WORK: 'work' };

const hq = { color, floors, type, surface, motion, modes };
export default hq;
